// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the "What Konvo knows" settings page (design/assistant-memory, option C · Profile).
// Konvo's picture of the person on top (avatar, name, the summary it writes from the notes and the
// key facts as chips), a box to tell it something, then the notes as flat white cards by topic and
// what the proactive engine learned. Desktop: two columns, hover a note for its date, Change and
// Forget. Phone: one column; tap a note for a sheet.

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {SettingsTabContainer} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {getCachedNumberFormat} from '@app/features/i18n/utils/IntlCache';
import CornerMemory from '@app/features/konvo_corner/state/CornerMemory';
import {addKnowsNote, openVipSettings} from '@app/features/konvo_knows/components/KnowsActions';
import {KnowsHistoryButton} from '@app/features/konvo_knows/components/KnowsHistory';
import {
	FACT_LABEL_DESCRIPTORS,
	LEARNED_HEADING_DESCRIPTOR,
	learnedTexts,
	M,
	TOPIC_DESCRIPTORS,
} from '@app/features/konvo_knows/components/KnowsMessages';
import {ForgottenRow, KnowsNoteRow, PhoneNoteRow} from '@app/features/konvo_knows/components/KnowsNoteRow';
import {KnowsNoteSheet} from '@app/features/konvo_knows/components/KnowsNoteSheet';
import styles from '@app/features/konvo_knows/components/KonvoKnows.module.css';
import KonvoKnows from '@app/features/konvo_knows/state/KonvoKnows';
import {
	groupNotes,
	type KnowsFactKind,
	type KnowsLearned,
	type KnowsNote,
	type KnowsPage,
	type KnowsTopic,
	LEFT_TOPICS,
	learnedView,
	RIGHT_TOPICS,
} from '@app/features/konvo_knows/utils/KnowsModel';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Avatar} from '@app/features/ui/components/Avatar';
import {
	BellSlashIcon,
	ClockIcon,
	IdentificationBadgeIcon,
	MapPinIcon,
	PlusIcon,
	SlidersHorizontalIcon,
	StarIcon,
	TranslateIcon,
} from '@app/features/ui/icons/Icons';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import Users from '@app/features/user/state/Users';
import {getCurrentLocale} from '@app/features/user/utils/LocaleUtils';
import {useLingui} from '@lingui/react/macro';
import {IconBriefcase} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {reaction} from 'mobx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useState} from 'react';

function FactIcon({kind}: {kind: KnowsFactKind}) {
	switch (kind) {
		case 'place':
			return <MapPinIcon size={14} />;
		case 'hours':
			return <ClockIcon size={14} />;
		case 'language':
			return <TranslateIcon size={14} />;
		case 'company':
			return <IconBriefcase size={14} stroke={1.75} />;
		case 'role':
			return <IdentificationBadgeIcon size={14} />;
	}
}

const ProfileCard = observer(({page, phone}: {page: KnowsPage; phone: boolean}) => {
	const {i18n} = useLingui();
	const user = Users.currentUser;
	const summary = page.summary;
	const avatar = user ? <Avatar user={user} size={phone ? 44 : 64} className={styles.profileAvatar} /> : null;
	const body = summary ? (
		<>
			<p className={styles.summary} data-flx="konvo-knows.profile.summary">
				{summary.text}
			</p>
			{summary.facts.length > 0 && (
				<div className={styles.facts} data-flx="konvo-knows.profile.facts">
					{summary.facts.map((fact) => (
						<span
							key={`${fact.kind}-${fact.text}`}
							className={styles.fact}
							title={i18n._(FACT_LABEL_DESCRIPTORS[fact.kind])}
						>
							<FactIcon kind={fact.kind} />
							{fact.text}
						</span>
					))}
				</div>
			)}
		</>
	) : (
		<p className={clsx(styles.summary, styles.summaryQuiet)} data-flx="konvo-knows.profile.summary-empty">
			{i18n._(page.summary_stale ? M.summaryUpdating : M.summaryEmpty)}
		</p>
	);
	if (phone) {
		return (
			<section className={clsx(styles.card, styles.profile, styles.profilePhone)} data-flx="konvo-knows.profile">
				<div className={styles.profileHead}>
					{avatar}
					<span className={styles.profileName}>{page.name}</span>
				</div>
				{body}
			</section>
		);
	}
	return (
		<section className={clsx(styles.card, styles.profile)} data-flx="konvo-knows.profile">
			{avatar}
			<div className={styles.profileBody}>
				<div className={styles.profileName}>{page.name}</div>
				{body}
			</div>
		</section>
	);
});

const AddBox = observer(({phone}: {phone: boolean}) => {
	const {i18n} = useLingui();
	const [text, setText] = useState('');
	const [busy, setBusy] = useState(false);
	const submit = async () => {
		if (busy || !text.trim()) return;
		setBusy(true);
		const ok = await addKnowsNote(i18n, text);
		setBusy(false);
		if (ok) setText('');
	};
	return (
		<label className={clsx(styles.card, styles.add)} data-flx="konvo-knows.add">
			<PlusIcon size={18} className={styles.addIcon} />
			<input
				className={styles.addInput}
				value={text}
				maxLength={300}
				placeholder={i18n._(phone ? M.addPlaceholderShort : M.addPlaceholder)}
				aria-label={i18n._(M.addLabel)}
				disabled={busy}
				enterKeyHint="done"
				onChange={(event) => setText(event.target.value)}
				onKeyDown={(event) => {
					if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
						event.preventDefault();
						void submit();
					}
				}}
				data-flx="konvo-knows.add.input"
			/>
		</label>
	);
});

function TopicCard({
	topic,
	notes,
	forgotten,
	phone,
	onOpen,
}: {
	topic: KnowsTopic;
	notes: ReadonlyArray<KnowsNote>;
	forgotten: ReadonlySet<string>;
	phone: boolean;
	onOpen: (note: KnowsNote) => void;
}) {
	const {i18n} = useLingui();
	if (notes.length === 0) return null;
	const asPerson = topic === 'people';
	return (
		<section className={styles.group} data-flx={`konvo-knows.topic.${topic}`}>
			<h3 className={styles.groupHeading}>
				<span>{i18n._(TOPIC_DESCRIPTORS[topic])}</span>
				{phone && <span className={styles.groupCount}>{notes.filter((note) => !forgotten.has(note.id)).length}</span>}
			</h3>
			<div className={styles.card}>
				{notes.map((note) =>
					forgotten.has(note.id) ? (
						<ForgottenRow key={note.id} note={note} />
					) : phone ? (
						<PhoneNoteRow key={note.id} note={note} asPerson={asPerson} onOpen={() => onOpen(note)} />
					) : (
						<KnowsNoteRow key={note.id} note={note} asPerson={asPerson} />
					),
				)}
			</div>
		</section>
	);
}

function LearnedIcon({entry}: {entry: KnowsLearned}) {
	switch (entry.type) {
		case 'tuning':
			return <SlidersHorizontalIcon size={15} />;
		case 'muted':
			return <BellSlashIcon size={15} />;
		case 'vips':
			return <StarIcon size={15} />;
	}
}

const LearnedRow = observer(({entry}: {entry: KnowsLearned}) => {
	const {i18n} = useLingui();
	const [busy, setBusy] = useState(false);
	const view = learnedView(entry);
	const texts = learnedTexts(i18n, view);
	const act = async () => {
		if (view.action === 'edit') {
			openVipSettings();
			return;
		}
		setBusy(true);
		const ok = await KonvoKnows.undoLearned(entry.id);
		setBusy(false);
		if (!ok) ToastCommands.error(i18n._(M.failed));
	};
	return (
		<div className={clsx(styles.row, styles.learnedRow)} data-flx={`konvo-knows.learned.${entry.type}`}>
			<span className={styles.learnedIcon} aria-hidden="true">
				<LearnedIcon entry={entry} />
			</span>
			<span className={styles.learnedText}>
				<b>{texts.title}</b>
				<small>{texts.sub}</small>
			</span>
			<button
				type="button"
				className={styles.pill}
				disabled={busy}
				onClick={() => void act()}
				data-flx={`konvo-knows.learned.${view.action}`}
			>
				{texts.action}
			</button>
		</div>
	);
});

function LearnedCard({learned}: {learned: ReadonlyArray<KnowsLearned>}) {
	const {i18n} = useLingui();
	if (learned.length === 0) return null;
	return (
		<section className={styles.group} data-flx="konvo-knows.learned">
			<h3 className={styles.groupHeading}>
				<span>{i18n._(LEARNED_HEADING_DESCRIPTOR)}</span>
			</h3>
			<div className={styles.card}>
				{learned.map((entry) => (
					<LearnedRow key={entry.id} entry={entry} />
				))}
			</div>
		</section>
	);
}

function confirmForgetAll(count: number, i18n: ReturnType<typeof useLingui>['i18n']) {
	ModalCommands.push(
		modal(() => (
			<ConfirmModal
				title={i18n._(M.forgetAllTitle)}
				description={i18n._(M.forgetAllBody, {count})}
				primaryText={i18n._(M.forgetAll)}
				primaryVariant="danger"
				onPrimary={async () => {
					if (await KonvoKnows.forgetAll()) ToastCommands.success(i18n._(M.forgotAll));
					else ToastCommands.error(i18n._(M.failed));
				}}
			/>
		)),
	);
}

const Footer = observer(({page, phone}: {page: KnowsPage; phone: boolean}) => {
	const {i18n} = useLingui();
	const number = getCachedNumberFormat(getCurrentLocale());
	return (
		<footer className={clsx(styles.footer, phone && styles.footerPhone)} data-flx="konvo-knows.footer">
			<span className={styles.footerText}>
				{i18n._(M.footer, {
					count: page.notes.length,
					size: number.format(page.size),
					max: number.format(page.max),
				})}
			</span>
			<span className={styles.footerActions}>
				{phone && <KnowsHistoryButton />}
				{page.notes.length > 0 && (
					<button
						type="button"
						className={clsx(styles.pill, styles.pillDanger)}
						onClick={() => confirmForgetAll(page.notes.length, i18n)}
						data-flx="konvo-knows.forget-all"
					>
						{i18n._(M.forgetAll)}
					</button>
				)}
			</span>
		</footer>
	);
});

export const KonvoKnowsTab = observer(() => {
	const {i18n} = useLingui();
	const phone = MobileLayout.enabled;
	const page = KonvoKnows.page;
	const [sheetNoteId, setSheetNoteId] = useState<string | null>(null);
	useEffect(() => {
		void KonvoKnows.load();
		// While this page is open the corner shows no "I'll remember that" card; it refreshes us instead.
		const detach = CornerMemory.attachKnowsPage({
			refresh: () => {
				if (!KonvoKnows.page) KonvoKnows.refresh();
			},
		});
		// The corner card's Change: open that note for editing (now, or when it asks while we are open).
		const stopFocus = reaction(
			() => CornerMemory.focusNoteId,
			(id) => {
				if (!id) return;
				const noteId = CornerMemory.takeFocusNote();
				if (!noteId) return;
				if (MobileLayout.enabled) setSheetNoteId(noteId);
				else KonvoKnows.setEditing(noteId);
				if (!KonvoKnows.page?.notes.some((note) => note.id === noteId)) void KonvoKnows.load();
			},
			{fireImmediately: true},
		);
		return () => {
			detach();
			stopFocus();
			KonvoKnows.setEditing(null);
		};
	}, []);
	const forgotten = new Set(KonvoKnows.recentlyForgotten.map((note) => note.id));
	const groups = page ? groupNotes([...page.notes, ...KonvoKnows.recentlyForgotten]) : null;
	const sheetNote = sheetNoteId ? (page?.notes.find((note) => note.id === sheetNoteId) ?? null) : null;

	let content: React.ReactNode = null;
	if (!page || !groups) {
		content = KonvoKnows.loadFailed ? (
			<div className={styles.failed} data-flx="konvo-knows.failed">
				<span>{i18n._(M.loadFailed)}</span>
				<button type="button" className={styles.pill} onClick={() => void KonvoKnows.load()}>
					{i18n._(M.retry)}
				</button>
			</div>
		) : null;
	} else {
		const topic = (name: KnowsTopic) => (
			<TopicCard
				key={name}
				topic={name}
				notes={groups[name]}
				forgotten={forgotten}
				phone={phone}
				onOpen={(note) => setSheetNoteId(note.id)}
			/>
		);
		content = (
			<>
				<ProfileCard page={page} phone={phone} />
				<AddBox phone={phone} />
				{page.notes.length === 0 && <p className={clsx(styles.quiet, styles.empty)}>{i18n._(M.empty)}</p>}
				{phone ? (
					<div className={styles.column}>
						{[...LEFT_TOPICS, ...RIGHT_TOPICS].map(topic)}
						<LearnedCard learned={page.learned} />
					</div>
				) : (
					<div className={styles.columns}>
						<div className={styles.column}>
							{LEFT_TOPICS.map(topic)}
							<LearnedCard learned={page.learned} />
						</div>
						<div className={styles.column}>{RIGHT_TOPICS.map(topic)}</div>
					</div>
				)}
				<Footer page={page} phone={phone} />
			</>
		);
	}

	return (
		<SettingsTabContainer>
			<div id="konvo-knows" className={clsx(styles.page, phone && styles.pagePhone)} data-flx="konvo-knows.page">
				{content}
			</div>
			{sheetNote && <KnowsNoteSheet key={sheetNote.id} note={sheetNote} onClose={() => setSheetNoteId(null)} />}
		</SettingsTabContainer>
	);
});

export default KonvoKnowsTab;
